/**
 * Session 50 §5f.bis — team-event recipient UX.
 *
 * When a teammate advances, the receiving PWA gets a TeamEvent over the
 * team WS channel. Rather than silently teleporting the player forward
 * (which feels like the app stole their work, per the Phase-2 design
 * review), we render a 1.5-second overlay toast and THEN advance. If the
 * player is mid-typing an answer, the banner variant preserves their draft.
 *
 * This module owns all recipient-side UX states from the plan's table:
 *   - Reading riddle → soft toast, auto-advance after 1.5s
 *   - Mid-typing answer → dismissible banner with draft preserved
 *   - In chat → banner at top of chat, don't force-close
 *   - CAUGHT_UP (reconnect merge) → celebratory "+N locations" toast
 *   - FINISH → calm overlay with 2 CTAs (don't cutover mid-typing)
 *
 * All toasts sit in a single body-attached container so they escape
 * view-level clipping/z-index (same pattern as SOS dialog + kebab menu).
 * Each toast has role="status" + aria-live so screen readers announce it.
 */

import type { TeamEvent } from '../api/team-sync';
import type { ViewLifecycle } from '../utils/view-lifecycle';

/** How long the soft "Команда решила загадку!" toast stays before the
 *  auto-advance fires. Matches the plan's table. */
const ADVANCE_TOAST_MS = 1500;
/** How long the "Команда догнала!" catch-up toast stays. Longer than
 *  ADVANCE so the player can register the delta. */
const CAUGHT_UP_TOAST_MS = 2000;
/** How long the session-expired toast stays. Long enough to read before
 *  the join redirect lands. */
const SESSION_EXPIRED_TOAST_MS = 3000;

/** Injected once, shared across all toast renders. Kept minimal so the
 *  PWA bundle doesn't grow — most styling borrows existing CSS vars. */
const STYLE_ID = 'aq-team-event-styles';

/**
 * v0.80.9 (2026-05-04) — global-singleton teardown audit follow-up to v0.80.7.
 * Module-local registry of every undismissed toast's dismiss fn. `showToast`
 * adds its dismiss into the set on render; the dismiss closure removes itself
 * on first invocation (idempotent — re-dismiss is a no-op). `dismissAllTeamToasts`
 * walks a snapshot and fires each.
 *
 * Why this exists: the body-attached `.aq-team-toast-root` is a global singleton
 * just like the connection-bar — it survives view swaps. Most toasts have an
 * `autoDismissMs` of 1.2-3s, but `handleAdvanceEvent`'s mid-typing variant has
 * CTA buttons and NO autoDismiss, so the banner persists until the user clicks.
 * If the player navigates away (kebab → leave / WS-driven re-route / tab swap +
 * router visibility flip), the banner stays on /join with broken callbacks.
 * Even the autoDismiss variants leak visibly for their remaining window onto
 * the next view. Same class as the connection-bar bug fixed in v0.80.7.
 */
const activeDismissals = new Set<() => void>();

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .aq-team-toast-root { position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 300; pointer-events: none; display: flex; flex-direction: column; gap: 8px; max-width: min(420px, calc(100vw - 32px)); }
    .aq-team-toast { pointer-events: auto; background: var(--bg-card, #2a2a2a); color: var(--text-primary, #fff); border: 1px solid var(--amber, #f5a623); border-radius: 12px; padding: 12px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.5); animation: aq-toast-in 180ms ease-out; }
    .aq-team-toast.aq-team-toast-finish { border-color: var(--success, #4caf50); }
    .aq-team-toast-title { font-weight: 600; margin-bottom: 4px; }
    .aq-team-toast-body { font-size: 14px; color: var(--text-secondary, #ccc); white-space: pre-wrap; }
    .aq-team-toast-cta { display: flex; gap: 8px; margin-top: 12px; }
    .aq-team-toast-cta button { flex: 1; min-height: 36px; border: 1px solid var(--amber, #f5a623); background: transparent; color: var(--text-primary, #fff); border-radius: 8px; font-size: 14px; cursor: pointer; padding: 6px 12px; }
    .aq-team-toast-cta button.aq-primary { background: var(--amber, #f5a623); color: #000; }
    @keyframes aq-toast-in { from { transform: translateY(-8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @media (prefers-reduced-motion: reduce) {
      .aq-team-toast { animation: none; }
    }
  `;
  document.head.appendChild(style);
}

function getRoot(): HTMLElement {
  ensureStyles();
  let root = document.querySelector<HTMLElement>('.aq-team-toast-root');
  if (!root) {
    root = document.createElement('div');
    root.className = 'aq-team-toast-root';
    document.body.appendChild(root);
  }
  return root;
}

interface ToastOpts {
  title: string;
  body?: string;
  /** "polite" for advances / catch-ups, "assertive" for finish so screen
   *  readers interrupt whatever they're announcing. */
  ariaLive: 'polite' | 'assertive';
  /** When set, toast auto-dismisses and the onAutoDismiss callback fires. */
  autoDismissMs?: number;
  onAutoDismiss?: () => void;
  /** When set, each button renders a CTA; click invokes the associated
   *  action and dismisses the toast. */
  ctas?: Array<{ label: string; primary?: boolean; onClick: () => void }>;
  /** Finish variant borders green. */
  variant?: 'default' | 'finish';
}

/** Render a single toast. Returns a dismiss function the caller can invoke
 *  (for example when the user navigates away or another event supersedes). */
function showToast(opts: ToastOpts): () => void {
  const root = getRoot();
  const el = document.createElement('div');
  el.className = 'aq-team-toast' + (opts.variant === 'finish' ? ' aq-team-toast-finish' : '');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', opts.ariaLive);

  const titleEl = document.createElement('div');
  titleEl.className = 'aq-team-toast-title';
  titleEl.textContent = opts.title;
  el.appendChild(titleEl);

  if (opts.body) {
    const bodyEl = document.createElement('div');
    bodyEl.className = 'aq-team-toast-body';
    bodyEl.textContent = opts.body;
    el.appendChild(bodyEl);
  }

  let dismissed = false;
  let timer: number | undefined;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    if (timer !== undefined) window.clearTimeout(timer);
    activeDismissals.delete(dismiss);
    el.remove();
  };
  activeDismissals.add(dismiss);

  if (opts.ctas && opts.ctas.length > 0) {
    const ctaRow = document.createElement('div');
    ctaRow.className = 'aq-team-toast-cta';
    for (const cta of opts.ctas) {
      const btn = document.createElement('button');
      if (cta.primary) btn.classList.add('aq-primary');
      btn.textContent = cta.label;
      btn.addEventListener('click', () => {
        dismiss();
        try { cta.onClick(); } catch (err) { console.error('toast cta error', err); }
      });
      ctaRow.appendChild(btn);
    }
    el.appendChild(ctaRow);
  }

  root.appendChild(el);

  if (opts.autoDismissMs !== undefined) {
    timer = window.setTimeout(() => {
      dismiss();
      if (opts.onAutoDismiss) {
        try { opts.onAutoDismiss(); } catch (err) { console.error('toast auto-dismiss error', err); }
      }
    }, opts.autoDismissMs);
  }

  return dismiss;
}

/**
 * Plan B Task 10 (2026-05-11) — view-scoped showToast wrapper. Renders the
 * toast via the existing `showToast` path AND registers the per-toast
 * `dismiss` closure with `lc.onDestroy`. When the owner view tears down
 * (route swap / kebab → leave / WS-driven re-route / visibility-driven
 * remount), `lc.cleanup()` runs each registered dismiss — same effect as
 * the v0.80.9 `dismissAllTeamToasts()` call inside destroyLocationView, but
 * routed through the lifecycle primitive instead of an explicit destroy
 * body.
 *
 * Why per-toast `dismiss` and NOT `bodyOverlays.register('team-toast',
 * dismissAllTeamToasts, lc)` — the bodyOverlays version sounds tempting
 * (matches Task 9 / auto-advance shape), but the replace-semantics inside
 * `bodyOverlays.register` would fire the SHARED `dismissAllTeamToasts`
 * sweep on the SECOND `showToastWithLc` call from the same view — the
 * sweep wipes the freshly-rendered toast too, breaking the stacking
 * semantics that team-event-toast was built for (PENALTY + GEO_VERIFIED
 * arriving in the same WS tick was a deliberate design point — see the
 * `team-event-toast-cleanup.test.ts::sweeps multiple stacked toasts` test).
 * The per-toast `dismiss` registration sidesteps this — each call appends
 * its OWN cleanup, so stacked toasts coexist until destroy. The individual
 * `dismiss` is also idempotent (its closure-local `dismissed` flag guards
 * against double-fire from auto-dismiss timer + lc.cleanup overlap).
 *
 * The five view-scoped `handleXxxEvent` exports route through this wrapper.
 * The LC-free exports (`showSessionExpiredToast`, `showErrorToast`,
 * `showCopyToast`) keep calling `showToast` directly — they're invoked from
 * non-view contexts (main.ts global auth-failure handler, auto-advance.ts
 * cross-view utility, lobby-view kebab + team-name copy actions) where
 * there is no `activeLc` to bind to. lobby-view's render registers
 * `dismissAllTeamToasts` via `activeLc.onDestroy` so its `showCopyToast`
 * usage still gets swept on destroy — see lobby-view.ts (Plan B Task 10).
 */
function showToastWithLc(opts: ToastOpts, lc: ViewLifecycle): () => void {
  const dismiss = showToast(opts);
  lc.onDestroy(dismiss);
  return dismiss;
}

/**
 * Handle an ADVANCE event on the current view. The caller passes callbacks
 * describing what to do on dismiss/action — keeps the toast dumb-UI,
 * leaves navigation to the view.
 *
 * - If `draft` is non-empty the player is mid-typing; render the banner
 *   variant so their work isn't destroyed.
 * - Otherwise render the soft 1.5s toast.
 */
export function handleAdvanceEvent(
  event: TeamEvent,
  opts: {
    draft?: string;
    onAdvance: () => void;
    onCancel?: () => void;
  },
  lc: ViewLifecycle,
): () => void {
  const submitter = event.submitter && event.submitter.trim().length > 0
    ? `${event.submitter} ответил`
    : 'Команда решила загадку';

  const draft = opts.draft ?? '';
  if (draft.length > 0) {
    // Mid-typing — draft-preservation banner with CTAs.
    return showToastWithLc({
      title: 'Команда ушла вперёд',
      body: `Ваш черновик: «${draft}»`,
      ariaLive: 'polite',
      ctas: [
        { label: 'Отменить', onClick: () => opts.onCancel?.() },
        { label: 'Перейти', primary: true, onClick: opts.onAdvance },
      ],
    }, lc);
  }

  // Reading riddle / passive context — soft toast + auto-advance.
  return showToastWithLc({
    title: submitter + '!',
    ariaLive: 'polite',
    autoDismissMs: ADVANCE_TOAST_MS,
    onAutoDismiss: opts.onAdvance,
  }, lc);
}

/**
 * CAUGHT_UP (offline reconnect merge). Celebratory toast shows the delta.
 * No auto-advance — the caller navigates synchronously after invoking this
 * because the server has already updated authoritative state.
 */
export function handleCaughtUpEvent(
  event: TeamEvent,
  opts: { onCompleteSync?: () => void } = {},
  lc: ViewLifecycle,
): () => void {
  const from = event.fromIndex ?? 0;
  const to = event.toIndex ?? event.newIndex ?? from;
  const delta = Math.max(0, to - from);
  return showToastWithLc({
    title: 'Команда догнала!',
    body: delta > 0 ? `+${delta} ${pluralLocations(delta)}` : 'Прогресс синхронизирован',
    ariaLive: 'polite',
    autoDismissMs: CAUGHT_UP_TOAST_MS,
    onAutoDismiss: opts.onCompleteSync,
  }, lc);
}

/**
 * FINISH. Non-auto-cutover by design — the recipient may be mid-typing or
 * mid-chat and forcing a view swap destroys their work. Two CTAs: go to
 * results now, or keep exploring the current view.
 */
export function handleFinishEvent(
  _event: TeamEvent,
  opts: { onGoToResults: () => void; onKeepExploring?: () => void },
  lc: ViewLifecycle,
): () => void {
  return showToastWithLc({
    title: 'Команда финишировала!',
    body: 'Результаты сохранены.',
    ariaLive: 'assertive',
    variant: 'finish',
    ctas: [
      { label: 'Продолжить осмотр', onClick: () => opts.onKeepExploring?.() },
      { label: 'К результатам', primary: true, onClick: opts.onGoToResults },
    ],
  }, lc);
}

/**
 * Session 50 post-ship fix — GEO_VERIFIED toast. A teammate found the
 * location (verified coordinates / address or hit the auto-navigate hint).
 * Show a short, non-intrusive toast and let the caller advance the phase
 * in the same tick. No CTAs — the jump is always correct for the team
 * (the teammate already proved the answer server-side).
 */
export function handleGeoVerifiedEvent(
  event: TeamEvent,
  opts: { onAdvance: () => void },
  lc: ViewLifecycle,
): () => void {
  const submitter = event.submitter?.trim();
  const title = submitter
    ? `${submitter} нашёл(нашла) место!`
    : 'Команда нашла место!';
  return showToastWithLc({
    title,
    body: 'Переходим к вводу кода.',
    ariaLive: 'polite',
    autoDismissMs: ADVANCE_TOAST_MS,
    onAutoDismiss: opts.onAdvance,
  }, lc);
}

/**
 * Session 66 — PENALTY toast. Fires when the organizer adjusts the team's
 * penaltyMs from the Monitoring kebab. Shows the signed delta and the new
 * cumulative total. Negative delta (bonus) is styled identically to positive
 * — organizer intent is the source of truth.
 *
 * `penaltyMsDelta` is the signed delta the server actually applied (after
 * server-side clamp-at-zero, so it can differ from what the organizer asked
 * for when the delta was more negative than the current total). Paired with
 * `penaltyMs` (new cumulative total) for the body line.
 */
export function handlePenaltyEvent(event: TeamEvent, lc: ViewLifecycle): () => void {
  const deltaMs = (event as unknown as { penaltyMsDelta?: number }).penaltyMsDelta ?? 0;
  const totalMs = event.penaltyMs ?? 0;
  const deltaMin = Math.round(deltaMs / 60_000);
  const totalMin = Math.round(totalMs / 60_000);
  const deltaLabel = deltaMin >= 0 ? `+${deltaMin}` : `${deltaMin}`;
  const title = deltaMin >= 0
    ? `Штраф ${deltaLabel} мин`
    : `Бонус ${deltaLabel} мин`;
  const body = totalMin > 0
    ? `Итого +${totalMin} мин`
    : 'Итого без штрафа';
  return showToastWithLc({
    title,
    body,
    ariaLive: 'polite',
    autoDismissMs: ADVANCE_TOAST_MS,
  }, lc);
}

/**
 * Session 68 — lightweight copy-feedback toast. Used by lobby kebab tap-to-copy
 * on the game code row. Reuses the same body-attached toast root (DESIGN.md
 * one-toast-surface rule) so visual + a11y behavior matches every other
 * team-event toast. Default 1200ms dismiss — long enough to register, short
 * enough not to stack if the user mashes the copy action.
 */
export function showCopyToast(label: string, durationMs: number = 1200): () => void {
  return showToast({
    title: label,
    ariaLive: 'polite',
    autoDismissMs: durationMs,
  });
}

/**
 * v0.79.18 — session-expired toast. Fired by main.ts listener after
 * detecting permanent auth failure (REST 401/403 or WS close 1008).
 * Reuses showToast — same body-attached root, same a11y/styling.
 * 3-second dismiss — long enough to read, short enough not to block
 * the join screen the user is being redirected to.
 *
 * Defaults to assertive aria-live so screen readers interrupt — auth
 * loss is a state change the user must be aware of.
 */
export function showSessionExpiredToast(label: string = 'Сессия устарела — войдите заново'): () => void {
  return showToast({
    title: label,
    ariaLive: 'assertive',
    autoDismissMs: SESSION_EXPIRED_TOAST_MS,
  });
}

/**
 * v0.86.2 — generic error toast for cross-view recoverable failures.
 * Currently used by auto-advance.ts to surface backend `GameStateException`
 * messages («Время этапа ещё не истекло», «Только капитан может пропустить
 * этап», «Ручной пропуск отключён организатором», network failures) that
 * were previously console.warn-only — backlog item 3 of the 2026-05-08
 * ultrareview audit.
 *
 * Assertive aria-live: failure is a state the user must register before
 * deciding to retry. 4-second dismiss: longer than session-expired (3s)
 * because the consequence is recoverable and longer messages need reading
 * time, shorter than 5s to avoid blocking subsequent UI.
 */
export function showErrorToast(label: string, durationMs: number = 4000): () => void {
  return showToast({
    title: label,
    ariaLive: 'assertive',
    autoDismissMs: durationMs,
  });
}

/** "локацию" / "локации" / "локаций" — ru-pluralize for toast copy. */
function pluralLocations(n: number): string {
  const abs = Math.abs(n) % 100;
  const tens = abs % 10;
  if (abs > 10 && abs < 20) return 'локаций';
  if (tens > 1 && tens < 5) return 'локации';
  if (tens === 1) return 'локацию';
  return 'локаций';
}

/**
 * v0.80.9 — Production-safe singleton sweep, called from view-destroy hooks
 * to prevent the toast root + active toasts from leaking into the next view.
 * Walks a snapshot of `activeDismissals` (each dismiss removes itself, so
 * direct iteration would skip entries) and fires each — that clears the
 * timer, removes the DOM, and cancels any pending auto-dismiss callback
 * (the cancelled callback is for the same view that's tearing down, so
 * dropping it is correct).
 *
 * The empty `.aq-team-toast-root` flex container is left in place — it's
 * invisible without children and recreating it on the next showToast costs
 * a `document.createElement` + `appendChild`. Cheap, no harm. The injected
 * `<style id="aq-team-event-styles">` is also kept — `ensureStyles` is
 * idempotent so leaving it doesn't matter.
 */
export function dismissAllTeamToasts(): void {
  const snapshot = Array.from(activeDismissals);
  for (const d of snapshot) {
    try { d(); } catch { /* best-effort */ }
  }
}

/** Test-only — clear all toasts + styles between Vitest cases. */
export function __resetToastsForTests(): void {
  activeDismissals.clear();
  document.querySelectorAll('.aq-team-toast, .aq-team-toast-root').forEach(el => el.remove());
  document.getElementById(STYLE_ID)?.remove();
}
